import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import './ChatPanel.css';
import { generateChatContext } from './Deal';
import { getBaseUrl } from './ApiContext';
import { forceReauth } from './AuthContext';

const baseUrl = getBaseUrl();

const ChatPanel = ({ dealInfo, onClose, highlights, setMemo, saveMemo }) => {
    const [chatContext, setChatContext] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [responseText, setResponseText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionHistory, setSessionHistory] = useState([]);
    const highlightsRef = useRef(null);
    const [replaceSections, setReplaceSections] = useState([]);
    const inputRef = useRef(null);
    // Held so the Stop button can abort the answer in flight, the same way
    // Ask the network does (NetworkChat.js).
    const abortRef = useRef(null);
    const [stopped, setStopped] = useState(false);
    const [lastHighlight, setLastHighlight] = useState(null);
    const [processedHighlights, setProcessedHighlights] = useState([]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        console.log('ChatPanel received dealInfo:', dealInfo);
        console.log('Highlights:', highlights);
        if (dealInfo) {
            const context = generateChatContext(dealInfo);
            setChatContext(context);
            console.log('Generated chat context:', context);
        } else {
            console.error('dealInfo is undefined in ChatPanel');
        }
    }, [dealInfo]);

    useEffect(() => {
        const addNewHighlights = () => {
            const uniqueHighlights = [...new Set(highlights)];
            const newHighlights = uniqueHighlights.filter(highlight => !processedHighlights.includes(highlight));

            if (newHighlights.length > 0) {
                setSessionHistory(prev => [
                    ...prev,
                    ...newHighlights.map(highlight => ({ type: 'highlight', content: highlight }))
                ]);
                setProcessedHighlights(prev => [...prev, ...newHighlights]);
                setLastHighlight(newHighlights[newHighlights.length - 1]);
            }
        };

        addNewHighlights();
    }, [highlights]);

    useEffect(() => {
        console.log('Session History:', sessionHistory);
    }, [sessionHistory]);

    const renderParagraph = useCallback(({ children }) => {
        const processNode = (node) => {
            if (typeof node === 'string') {
                return node.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                        {line}
                        <br />
                    </React.Fragment>
                ));
            }
            return node;
        };

        return (
            <React.Fragment>
                {Array.isArray(children)
                    ? children.map((child, index) => <React.Fragment key={index}>{processNode(child)}</React.Fragment>)
                    : processNode(children)}
            </React.Fragment>
        );
    }, []);

    const renderSessionHistory = () => {
        // Skip the first entry if it's a highlight
        const startIndex = sessionHistory.length > 0 && sessionHistory[0].type === 'highlight' ? 1 : 0;

        return sessionHistory.slice(startIndex).map((item, index) => {
            if (item.type === 'highlight' && item.content.trim() === '') {
                return null; // Skip empty highlights
            }
            return (
                <div key={index} className={`message ${item.type}`}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                            p: renderParagraph
                        }}
                    >
                        {item.content}
                    </ReactMarkdown>
                </div>
            );
        }).filter(Boolean); // Remove null entries
    };

    // Return starts a NEW LINE and does not send (Afnaan, 2026-09-17, about
    // the same thing on Ask the network). A question about a deal is often
    // several lines, and a stray return used to send half of one. Sending is
    // the Send button, or cmd/ctrl + return. This is a textarea for the same
    // reason the other composer is.
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            sendMessage(inputMessage);
        }
    };

    // The box grows with what is typed: a textarea keeps one row's height
    // whatever is in it, so everything past the first line would be typed
    // out of sight. Capped, then it scrolls.
    useEffect(() => {
        const box = inputRef.current;
        if (!box) return;
        box.style.height = 'auto';
        box.style.height = `${Math.min(box.scrollHeight, 140)}px`;
    }, [inputMessage]);

    const stop = () => {
        abortRef.current?.abort();
    };

    const detectMarkdownSections = (response) => {
        const sectionRegex = /^#+\s+(.+)$/gm;
        const sections = [];
        let match;
        while ((match = sectionRegex.exec(response)) !== null) {
            sections.push({
                title: match[1],
                content: response.slice(match.index),
                selected: false
            });
        }
        return sections;
    };

    const handleReplaceSection = (index) => {
        setReplaceSections(prev =>
            prev.map((section, i) =>
                i === index ? { ...section, selected: !section.selected } : section
            )
        );
    };

    const applyReplacements = () => {
        setMemo(prevMemo => {
            let newMemo = prevMemo;
            replaceSections.forEach(section => {
                if (section.selected) {
                    const sectionRegex = new RegExp(`#+\\s+${section.title}[\\s\\S]*?(?=\\n#+\\s+|$)`, 'i');
                    newMemo = newMemo.replace(sectionRegex, section.content.trim());
                }
            });
            return newMemo;
        });
        setReplaceSections([]);
        saveMemo(); // Call the saveMemo function after applying replacements
    };

    const sendMessage = async (message) => {
        // One answer at a time. Without this, every press of Send while one
        // was running started another, and the Send button had no idea an
        // answer was in flight.
        if (!message?.trim() || isLoading) return;
        setIsLoading(true);
        setStopped(false);
        // Cleared here rather than when the answer lands, so the box is
        // empty while you wait. Stopping puts it back.
        setInputMessage('');
        let accumulatedResponse = '';

        setSessionHistory(prev => [...prev, { type: 'user', content: message }]);

        // Use the lastHighlight state in the message
        const fullMessage = `
Company and People Information:
${JSON.stringify(dealInfo, null, 2)}

${lastHighlight ? `Last Highlight:\n${lastHighlight}\n\n` : ''}
User message:
${message}
        `.trim();

        try {
            const founderIds = dealInfo?.founders?.map(founder => founder.id) || [];

            const controller = new AbortController();
            abortRef.current = controller;
            const response = await fetch(`${baseUrl}/api/chat`, {
                method: 'POST',
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                },
                body: JSON.stringify({
                    message: fullMessage,
                    chatHistory,
                    founder_ids: founderIds,
                    company_id: dealInfo?.id, // Assuming the company ID is stored in dealInfo.id
                    files: dealInfo?.files || [],
                    deal_id: dealInfo?.id // Assuming deal_id is the same as company_id
                }),
            });

            if (response.status === 401) {
                forceReauth();
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                console.log('Received chunk:', chunk); // Log the raw chunk

                const lines = chunk.split('\n\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const content = line.slice(6).trim();
                        if (content === '[DONE]') {
                            console.log('Stream completed');
                            break;
                        }
                        if (content) {
                            try {
                                const jsonData = JSON.parse(content);
                                if (jsonData.type === 'content') {
                                    accumulatedResponse += jsonData.content;
                                    setResponseText(accumulatedResponse);
                                } else {
                                    console.warn('Unexpected data type:', jsonData.type);
                                }
                            } catch (error) {
                                console.error('Error parsing JSON:', error);
                            }
                        }
                    }
                }
            }

            console.log('Final accumulated response:', accumulatedResponse);

            const detectedSections = detectMarkdownSections(accumulatedResponse);
            setReplaceSections(detectedSections);

            setChatHistory(prev => [
                ...prev,
                { role: 'user', content: message },
                { role: 'assistant', content: accumulatedResponse }
            ]);
            setSessionHistory(prev => [...prev, { type: 'assistant', content: accumulatedResponse }]);
            setResponseText('');
        } catch (error) {
            // Stopping is a choice, not a failure: the message comes back to
            // the box, the half-written answer goes, and the panel says so
            // rather than leaving the message to vanish.
            if (error.name === 'AbortError') {
                setInputMessage(message);
                setSessionHistory(prev => (
                    prev.length && prev[prev.length - 1].type === 'user'
                    && prev[prev.length - 1].content === message
                        ? prev.slice(0, -1)
                        : prev));
                setResponseText('');
                setStopped(true);
                return;
            }
            console.error('Error:', error);
            setResponseText('An error occurred while fetching the response.');
        } finally {
            abortRef.current = null;
            setIsLoading(false);
        }
    };

    return (
        <div className="chat-panel">
            <div className="chat-panel-header">
                <h3>Chat</h3>
                <button
                    type="button"
                    className="panel-close"
                    onClick={onClose}
                    aria-label="Close chat"
                >
                    &times;
                </button>
            </div>
            <div className="chat-history">
                {renderSessionHistory()}
                {responseText && (
                    <div className="message assistant">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={{
                                p: renderParagraph
                            }}
                        >
                            {responseText}
                        </ReactMarkdown>
                    </div>
                )}
                {replaceSections.length > 0 && (
                    <div className="replace-sections">
                        <h4>Replace sections:</h4>
                        {replaceSections.map((section, index) => (
                            <div key={index}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={section.selected}
                                        onChange={() => handleReplaceSection(index)}
                                    />
                                    {section.title}
                                </label>
                            </div>
                        ))}
                        <button onClick={applyReplacements}>Apply Replacements</button>
                    </div>
                )}
            </div>
            <div className="chat-input-area">
                <div className="chat-input-row">
                    <textarea
                        ref={inputRef}
                        rows={1}
                        placeholder="Type your message..."
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                    />
                    {/* Stop sits BESIDE Send, which stays put and goes dead
                        while an answer runs. On Ask the network the two were
                        the same pixels, so the second press stopped the
                        answer and immediately asked it again. */}
                    {isLoading && (
                        <button type="button" className="chat-stop" onClick={stop}>
                            Stop
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => sendMessage(inputMessage)}
                        disabled={isLoading || !inputMessage.trim()}
                    >
                        Send
                    </button>
                </div>
                <p className="chat-input-hint">
                    {stopped
                        ? 'Stopped. Your message is back in the box.'
                        : 'Return starts a new line. \u2318 + return sends.'}
                </p>
            </div>
        </div>
    );
};

export default ChatPanel;